/* @flow */

import * as React from 'react';

// Hooks
import { useIntl } from 'react-intl';
import { useButton } from 'react-aria';

// Styles
import styles from './NotificationsMenu.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Utils
import messages from 'src/shared/app/notification/l10n/notificationL10n.json';
import { VARIANT } from 'src/shared/ui/component/button/Button';
import trackingEvents from 'src/shared/core/service/tracking/trackingEvents';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import { getNotificationId } from '@braindate/domain/lib/notification/util';
import { notificationsMenuId } from 'src/shared/app/nav/settings/navSettings';
import {
  useClearAllNotificationsMutation,
  useGetNotificationsQuery,
} from 'src/shared/app/base/api/endpoint/notificationsEndpoint';

// Components
import Popover from '@mui/material/Popover';
import FeedQuery from 'src/shared/ui/component/data-display/FeedQuery';
import EmptyNotificationFeed from 'src/shared/app/notification/component/main/EmptyNotificationFeed';
import NotificationCardSkeleton from 'src/shared/app/notification/component/card/NotificationCardSkeleton';
import NotificationCard from 'src/shared/app/notification/component/card/NotificationCard';

const useStyles = createUseThemeStyles(styles);

type Props = {
  children: React.Node,
  isOpened: boolean,
  setIsOpened: (newOpenedState: boolean) => void,
  className?: string,
  withRoundedChild?: boolean,
};

const NotificationsMenu = ({
  children,
  isOpened,
  setIsOpened,
  className,
  withRoundedChild,
}: Props) => {
  /*
  |----------------------------------------------------------------------------
  | States
  |----------------------------------------------------------------------------
  */

  const anchorRef = React.useRef();

  const { data } = useGetNotificationsQuery();
  const notificationsCount = data?.count || 0;

  const [clearAllNotifications, { isLoading: isClearingAll }] =
    useClearAllNotificationsMutation();
  /*
  |----------------------------------------------------------------------------
  | Hooks
  |----------------------------------------------------------------------------
  */

  const intl = useIntl();
  const classes = useStyles({ withRoundedChild });

  /*
  |----------------------------------------------------------------------------
  | Actions
  |----------------------------------------------------------------------------
  */

  const onClearAllClick = () => {
    trackingEvents.notification.readAll('Top button');
    clearAllNotifications();
  };

  /*
  |----------------------------------------------------------------------------
  | Elements
  |----------------------------------------------------------------------------
  */

  const { buttonProps: actionButtonProps } = useButton({
    onPress: () => setIsOpened(true),
    elementType: 'span',
    'aria-label': intl.formatMessage(messages.toggle),
    'aria-controls': isOpened ? notificationsMenuId : null,
  });

  return (
    <div className={className}>
      <span
        {...actionButtonProps}
        className={classes.button}
        ref={anchorRef}
        tabIndex={0}
      >
        {children}
      </span>

      <Popover
        id={notificationsMenuId}
        open={isOpened}
        anchorEl={anchorRef.current && anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={() => setIsOpened(false)}
      >
        <div className={classes.notificationsMenu}>
          <header className={classes.header}>
            <h3 className={classes.title}>
              {intl.formatMessage(messages.titlePage)}
            </h3>

            <SecondaryButton
              onClick={onClearAllClick}
              size="small"
              isFetching={isClearingAll}
              isDisabled={!notificationsCount}
              layoutClass={classes.actionButton}
            >
              {intl.formatMessage(messages.clearAll)}
            </SecondaryButton>
          </header>

          <div className={classes.feed}>
            <FeedQuery
              useQuery={useGetNotificationsQuery}
              manualConfig={{ isActive: true, variant: VARIANT.SECONDARY }}
              emptyComponent={<EmptyNotificationFeed small />}
              params={{ limit: 5 }}
              isCompact
              renderItem={(notification) => (
                <NotificationCard
                  notification={notification}
                  key={getNotificationId(notification)}
                />
              )}
              renderSkeleton={() => <NotificationCardSkeleton />}
            />
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default React.memo<Props>(NotificationsMenu);
